import { COLOR_ETHNICITY } from '../../components/helpers';
import { PrescricaoCidLme } from '../prescription/service';
import { Yup } from '../../helpers/Yup';
import { validateCPF } from '../../helpers/cpf';
import { validateCNS } from '../../helpers/cns';
import { onlyDigits } from '../../helpers/string';

export enum DOCUMENT_TYPE {
  CPF = 'CPF',
  CNS = 'CNS',
}

export interface ProfessionalForm {
  CNESNumber: string;
  establishment: string;
  doctorName: string;
  doctorCNS: string;
}

export interface PatientForm {
  patientName: string;
  patientSocialName: string;
  parentName: string;
  patientWeight: number | null;
  patientHeight: number | null;
  isIncapable: boolean;
  parentNameCapacityCertificate: string;
  ethnicity: COLOR_ETHNICITY;
  indigenousEthnicity: string;
  phoneNumber: string;
  docType: DOCUMENT_TYPE;
  patientDocNumber: string;
  patientEmail: string;
  isFillingFieldsDigitally: boolean;
}

export const RADIO_VALUES = {
  ACTIVE: '1',
  NOT_ACTIVE: '0',
};

export const PatientFormSchema = {
  patientName: Yup.string().required('Informe um nome válido'),
  parentName: Yup.string().required('Informe um nome válido'),
  patientWeight: Yup.number()
    .positive('Informe um peso válido')
    .required('Informe um peso válido')
    .typeError('Informe um peso válido'),
  patientHeight: Yup.number()
    .positive('Informe uma altura válida')
    .required('Informe uma altura válida')
    .typeError('Informe uma altura válida'),
  parentNameCapacityCertificate: Yup.string().when('isIncapable', {
    is: true,
    then: (schema) => schema.required('Informe um nome válido'),
    otherwise: (schema) => schema,
  }),
  ethnicity: Yup.string().when('isFillingFieldsDigitally', {
    is: true,
    then: (schema) =>
      schema
        .required('Informe uma etnia válida')
        .test('validate ethnicity', 'Informe uma etnia válida', function (value) {
          return value !== COLOR_ETHNICITY.NaoInformado;
        }),
    otherwise: (schema) => schema,
  }),
  indigenousEthnicity: Yup.string().when(['ethnicity', 'isFillingFieldsDigitally'], {
    is: (ethnicity: string, isFillingFieldsDigitally: boolean) => 
      ethnicity === COLOR_ETHNICITY.Indígena && isFillingFieldsDigitally,
    then: (schema) => schema.required('Informe uma etnia válida'),
    otherwise: (schema) => schema,
  }),
  phoneNumber: Yup.string().when('isFillingFieldsDigitally', {
    is: true,
    then: (schema) => schema.required('Informe um telefone válido'),
    otherwise: (schema) => schema,
  }),
  patientDocNumber: Yup.string().when('isFillingFieldsDigitally', {
    is: true,
    then: (schema) =>
      schema
        .required('Informe um documento válido')
        .test('validate document', 'Informe um documento válido', function (patientDocNumber) {
          const { docType } = this.parent;
          const document = onlyDigits(patientDocNumber || '');
          if (!document) return false;
          
          if (docType === DOCUMENT_TYPE.CPF) {
            return validateCPF(document);
          } else if (docType === DOCUMENT_TYPE.CNS) {
            return validateCNS(document);
          }
          return false;
        }),
    otherwise: (schema) => schema,
  }),
  patientEmail: Yup.string().when('isFillingFieldsDigitally', {
    is: true,
    then: (schema) => schema.required('Informe um e-mail válido').email('Informe um e-mail válido'),
    otherwise: (schema) => schema,
  }),
};

export const ProfessionalDataFormSchema = {
  CNESNumber: Yup.string().required('Informe um CNES válido'),
  establishment: Yup.string().required('Informe um estabelecimento válido'),
  doctorName: Yup.string().required('Informe um nome válido'),
  doctorCNS: Yup.string()
    .required('Informe um CNS válido')
    .test('validate cpf', 'Informe um CNS válido', function (doctorCNS) {
      if (!doctorCNS) return false;
      return validateCNS(doctorCNS);
    }),
};

export const validatePrescriptionIcdSmr = (prescriptionIcdSmr: PrescricaoCidLme) => {
  const hasAnamnesis = !!prescriptionIcdSmr.anamnese;
  const hasDescription = !prescriptionIcdSmr.tratamentoAnterior || !!prescriptionIcdSmr.descricaoTratamento;

  const hasValidUnits = prescriptionIcdSmr.medicamentosLme.every((medicine) => {
    return !!medicine.MedicamentosUnidadeLme[0];
  });

  return hasAnamnesis && hasDescription && hasValidUnits;
};
